import { useSearchParams } from "react-router-dom";

/**
 * Hook for managing URL search parameters with automatic cleanup of empty/default values
 *
 * @returns Object with searchParams and updateUrl function
 *
 * @example
 * const { searchParams, updateUrl } = useUrlParams();
 *
 * // Update multiple params at once
 * updateUrl({ status: 'active', page: '1' });
 *
 * // Clear a param by passing empty string, "all", or "false"
 * updateUrl({ status: 'all' }); // removes status param
 */
export function useUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Update URL search parameters, automatically removing empty or default values
   * - Empty strings, "all", and "false" values are removed from URL
   * - Other values are set in the URL
   */
  const updateUrl = (updates: Record<string, string>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== "all" && value !== "" && value !== "false") {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    });
  };

  return {
    searchParams,
    updateUrl,
  };
}
